// interceptors/error.interceptor.ts
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NavigateRole } from 'src/app/data/enums/navigate-role';
import { AppUtilsService } from 'src/app/services/app-utils.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { WorkFlowService } from 'src/app/services/work-flow.service';

// Define error types
export enum TransactionErrorType {
  EXPIRED = 'ONBOARDING.TRANSACTION_EXPIRED',
  REJECTED = 'ONBOARDING.TRANSACTION_REJECTED',
  APPROVED = 'ONBOARDING.TRANSACTION_APPROVED',
  NEED_RESUME = 'ONBOARDING.TRANSACTION_NOT_BELONG_TO_USER',
  APPROVED_ON_ANOTHER_DEVICE = 'ONBOARDING.TRANSACTION_ALREADY_APPROVED_ON_ANOTHER_DEVICE',
  VERIFIED_ON_ANOTHER_DEVICE = 'ONBOARDING.TRANSACTION_ALREADY_VERIFIED_ON_ANOTHER_DEVICE',//dopa passed
}

interface ErrorResponse {
  error: string;
  data?: {
    redirect_url?: string;
    message?: string;
    [key: string]: any;
    txn_id?: string; // ALREADY_APPROVED_ON_ANOTHER_DEVICE: txn ของผู้ชนะ
    type?: string;   // ALREADY_APPROVED_ON_ANOTHER_DEVICE: 'TRANSACTION_APPROVED_ON_ANOTHER_DEVICE'
  };
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly WHITELIST_URLS = ['/auth', '/login', '/register', '/resume'];
  private readonly SELF_HANDLED_RESUME_URLS = [
    '/onboarding/identity/verify-id-number', // alert confirm before resume
    '/onboarding/start-kyc'
  ];

  constructor(
    private router: Router,
    private appUtils: AppUtilsService,
    private injector: Injector,
    private sharedDataService: SharedDataService,
    protected modalController: ModalController
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // ตรวจสอบว่า URL อยู่ใน whitelist หรือไม่
    const isWhitelisted = this.WHITELIST_URLS.some(url =>
      request.url.includes(url)
    );

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // ถ้าเป็น URL ที่อยู่ใน whitelist หรือเป็น 401 ให้ส่ง error ต่อไป
        if (isWhitelisted || error.status === HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          console.error('Client error:', error.error.message);
          return throwError(() => error);
        }

        // Handle server-side errors
        return this.handleServerError(error);
      })
    );
  }

  private isSelfHandled(url: string | null): boolean {
    return this.SELF_HANDLED_RESUME_URLS.some(u => url?.includes(u));
  }
  private handleServerError(error: HttpErrorResponse): Observable<never> {
    const errorResponse: ErrorResponse = error.error;
    const { error: errorCode, data } = errorResponse;

    // ตรวจสอบ Transaction Error ก่อน
    if (errorCode) {
      switch (errorCode) {
        case TransactionErrorType.EXPIRED:
          this.handleExpiredTransaction(data);
          return throwError(() => error);

        case TransactionErrorType.REJECTED:
          this.handleRejectedTransaction(error);
          return throwError(() => error);

        case TransactionErrorType.APPROVED:
          this.handleApprovedTransaction(data);
          return throwError(() => error);

        case TransactionErrorType.NEED_RESUME:
          if (!this.isSelfHandled(error.url)) this.handleNeedResume();
          return throwError(() => error);

        case TransactionErrorType.VERIFIED_ON_ANOTHER_DEVICE:
          if (!this.isSelfHandled(error.url)) this.handleNeedResume(data);
          return throwError(() => error);

        case TransactionErrorType.APPROVED_ON_ANOTHER_DEVICE:
          if (!this.isSelfHandled(error.url)) this.handleCompletedOnAnotherDevice(error);
          return throwError(() => error);
      }
    }

    // ถ้าไม่ใช่ Transaction Error ให้จัดการตาม HTTP Status Code
    let errorMessage = '';

    switch (error.status) {
      // case HttpStatusCode.BadRequest:
      //   errorMessage = 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลอีกครั้ง';
      //   break;
      case HttpStatusCode.Forbidden:
        errorMessage = 'ไม่มีสิทธิ์เข้าถึงข้อมูล';
        break;
      case HttpStatusCode.NotFound:
        errorMessage = 'ไม่พบข้อมูลที่ต้องการ';
        break;
      case HttpStatusCode.TooManyRequests:
        errorMessage = 'มีการร้องขอข้อมูลมากเกินไป กรุณาลองใหม่ภายหลัง';
        break;
      case HttpStatusCode.InternalServerError:
        errorMessage = 'เกิดข้อผิดพลาดจากระบบ กรุณาลองใหม่ภายหลัง';
        break;
    }

    // แสดง error message
    if (errorMessage) {
      this.appUtils.showToast({
        message: errorMessage + ' ' + error.url,
        position: 'top',
      });
    }

    return throwError(() => error);
  }

  private async handleExpiredTransaction(data?: any) {
    this.sharedDataService.setData('topic', 'expired');
    await this.modalController.dismiss({}, 'error');
  }

  private async handleRejectedTransaction(error?: any) {
    // Set the rejection status in shared data
    this.sharedDataService.setData('topic', 'rejected');
    this.sharedDataService.setData('error-response', error);
    // Attempt to dismiss the modal
    await this.modalController.dismiss({}, 'error');
  }

  private async handleApprovedTransaction(data?: any): Promise<void> {
    await this.router.navigate(['/end']);
  }

  private async handleNeedResume(data?: { txn_id?: string; type?: string }): Promise<void> {
    const navigationService = this.injector.get(NavigationService);
    const workFlowService = this.injector.get(WorkFlowService);

    // อยู่ใน resume flow แล้ว ไม่ต้อง resume ซ้ำ (กัน request ค้างที่ error ตามมาทีหลัง)
    if (navigationService.is_resume) return;

    // ไม่มี modal เปิดอยู่ = มาจากช่วง startKyc ซึ่ง onStartKyc ใน HomePage จัดการเองอยู่แล้ว
    const top = await this.modalController.getTop();
    if (!top) return;

    await top.dismiss(
      data?.txn_id
        ? { txn_id: data.txn_id, type: data.type }   // bulk: txn ของผู้ชนะ
        : { txn_id: workFlowService.txnId },
      NavigateRole.FORCE_RESUME_WITH_TYPE
    );
  }

  private async handleCompletedOnAnotherDevice(error: HttpErrorResponse) {
    this.sharedDataService.setData('topic', NavigateRole.COMPLETED_ON_ANOTHER_DEVICE);
    this.sharedDataService.setData('error-response', error);
    const top = await this.modalController.getTop();
    await top?.dismiss({}, NavigateRole.ERROR);
  }
}
